import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { BottomNav, buildNavItems } from '@/components/mobile-navigation/BottomNav';
import { DesktopStage } from '@/components/mobile-shell/DesktopStage';
import { resolveContext, useSession } from '@/state/session';
import { db } from '@/mocks/db';

/**
 * Authenticated application shell.
 *
 * Provides the skip link, the single <main> landmark, the bottom navigation
 * and route-change focus management. Focus moves to the main region on every
 * navigation so keyboard and screen-reader users are not stranded at the top
 * of the document — and the page title is kept in sync for orientation.
 */
export function AppLayout() {
  const location = useLocation();
  const mainRef = useRef<HTMLElement | null>(null);
  const firstRender = useRef(true);
  const userId = useSession((s) => s.userId);
  const companyId = useSession((s) => s.companyId);

  const resolved = resolveContext();

  // Derived from the mutable mock store, so it is recomputed every render
  // rather than memoised — an approval decided elsewhere must clear the badge
  // immediately, and memoising on pathname would only mask that.
  const approvalCount =
    userId && companyId
      ? db().approvals.filter(
          (a) =>
            a.status === 'pending' &&
            a.approverIds.includes(userId) &&
            db().payments.some((p) => p.id === a.objectId && p.companyId === companyId),
        ).length
      : 0;

  useEffect(() => {
    // Skip on first paint: moving focus on load would fight the browser.
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    mainRef.current?.focus();
  }, [location.pathname]);

  const navItems = resolved ? buildNavItems(resolved.entitlements, { approvals: approvalCount }) : [];

  return (
    <DesktopStage>
      <div className="tm-shell">
        <a className="tm-skip-link" href="#tm-main">
          Skip to main content
        </a>
        <main id="tm-main" className="tm-main" ref={mainRef} tabIndex={-1} key={location.pathname}>
          <Outlet />
        </main>
        {navItems.length > 0 ? <BottomNav items={navItems} /> : null}
      </div>
    </DesktopStage>
  );
}

/** Shell for unauthenticated routes: no bottom navigation, no company context. */
export function AuthLayout() {
  const location = useLocation();
  return (
    <DesktopStage>
      <div className="tm-shell">
        <a className="tm-skip-link" href="#tm-main">
          Skip to main content
        </a>
        <main
          id="tm-main"
          className="tm-main tm-main--no-nav"
          tabIndex={-1}
          key={location.pathname}
        >
          <Outlet />
        </main>
      </div>
    </DesktopStage>
  );
}
